import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TradingViewWidget from '../components/TradingViewWidget';
import { getApiUrl } from '../config/api';
import websocketService from '../services/websocketService';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  ListSubheader,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

import { useAuth } from '../contexts/AuthContext';

// Categorized trading symbols
const tradingSymbols = [
  {
    category: 'Major Forex Pairs',
    symbols: [
      { symbol: 'EURUSD', name: 'EUR/USD' },
      { symbol: 'GBPUSD', name: 'GBP/USD' },
      { symbol: 'USDJPY', name: 'USD/JPY' },
      { symbol: 'USDCHF', name: 'USD/CHF' },
      { symbol: 'AUDUSD', name: 'AUD/USD' },
      { symbol: 'USDCAD', name: 'USD/CAD' },
      { symbol: 'NZDUSD', name: 'NZD/USD' },
    ]
  },
  {
    category: 'Metals',
    symbols: [
      { symbol: 'XAUUSD', name: 'XAU/USD (Gold)' },
    ]
  },
  // {
  //   category: 'Indices',
  //   symbols: [
  //     { symbol: 'NAS100USD', name: 'US100 (NASDAQ 100)' },
  //     { symbol: 'US30USD', name: 'US30 (Dow Jones 30)' },
  //   ]
  // },
  {
    category: 'Cryptocurrencies',
    symbols: [
      { symbol: 'BTCUSDT', name: 'BTC/USDT (Bitcoin)' },
      { symbol: 'ETHUSDT', name: 'ETH/USDT (Ethereum)' },
    ]
  }
];

// Flatten all symbols for easy access
const allSymbols = tradingSymbols.flatMap(group => group.symbols);

// Helper function to get phase badge properties
const getPhaseBadge = (phase) => {
  switch (phase) {
    case 'Phase1':
      return { label: 'P1', color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.1)' };
    case 'Phase2':
      return { label: 'P2', color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.1)' };
    case 'Funded':
      return { label: 'Funded', color: '#ffd700', bgColor: 'rgba(255, 215, 0, 0.1)' };
    case 'Failed':
      return { label: 'Failed', color: '#f44336', bgColor: 'rgba(244, 67, 54, 0.1)' };
    default:
      return { label: phase || 'Unknown', color: '#9e9e9e', bgColor: 'rgba(158, 158, 158, 0.1)' };
  }
};

// Helper function to determine market type based on symbol
const getMarketType = (symbol) => {
  // Remove OANDA: prefix if present
  const cleanSymbol = symbol.replace('OANDA:', '');
  
  // Forex pairs
  if (['EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD'].includes(cleanSymbol)) {
    return 'forex';
  }
  // Metals
  if (['XAUUSD'].includes(cleanSymbol)) {
    return 'commodities';
  }
  // Indices
  if (['NAS100USD', 'US30USD'].includes(cleanSymbol)) {
    return 'index';
  }
  // Cryptocurrencies
  if (['BTCUSDT', 'ETHUSDT'].includes(cleanSymbol)) {
    return 'crypto';
  }
  // Default to forex
  return 'forex';
};

// Comprehensive Profit/Loss Calculator
const calculateProfit = (instrument, tradeType, entryPrice, exitPrice, lotSize) => {
  const cleanSymbol = instrument.replace(/^[A-Z]+:/, '').toUpperCase();
  
  // Determine price movement based on trade type
  let priceMove;
  if (tradeType.toLowerCase() === 'buy') {
    priceMove = exitPrice - entryPrice;
  } else {
    priceMove = entryPrice - exitPrice;
  }
  
  let profit = 0;
  
  // 1. FOREX MAJORS WHERE USD IS QUOTE CURRENCY
  if (['EURUSD', 'GBPUSD', 'AUDUSD', 'NZDUSD'].includes(cleanSymbol)) {
    const pipSize = 0.0001;
    const pipValue = 10 * lotSize;
    const pips = priceMove / pipSize;
    profit = pips * pipValue;
  }
  
  // 2. FOREX MAJORS WHERE USD IS BASE CURRENCY
  else if (cleanSymbol === 'USDJPY') {
    const pipSize = 0.01;
    const pipValue = (10 * lotSize) / entryPrice;
    const pips = priceMove / pipSize;
    profit = pips * pipValue * 100; // Multiply by 100 to get correct value
  }
  else if (['USDCHF', 'USDCAD'].includes(cleanSymbol)) {
    const pipSize = 0.0001;
    const pipValue = (10 * lotSize) / entryPrice;
    const pips = priceMove / pipSize;
    profit = pips * pipValue;
  }
  
  // 3. GOLD (XAUUSD)
  else if (cleanSymbol === 'XAUUSD') {
    const pipSize = 0.01;
    const pipValue = 1 * lotSize;
    const pips = priceMove / pipSize;
    profit = pips * pipValue;
  }
  
  // 4. CRYPTO (BTCUSDT, ETHUSDT, BTCUSD, ETHUSD)
  else if (['BTCUSDT', 'ETHUSDT', 'BTCUSD', 'ETHUSD'].includes(cleanSymbol)) {
    const pipSize = 1.0; // Changed from 0.10 to 1.0
    const pipValue = 1 * lotSize;
    const units = priceMove / pipSize;
    profit = units * pipValue;
  }
  
  // Default fallback (should not reach here)
  else {
    const pipSize = 0.0001;
    const pipValue = 10 * lotSize;
    const pips = priceMove / pipSize;
    profit = pips * pipValue;
  }
  
  // Round to 2 decimals
  return Math.round(profit * 100) / 100;
};

// Risk/Reward Calculator
const calculateRiskReward = (instrument, entryPrice, stopLoss, takeProfit, lotSize) => {
  if (!entryPrice || !stopLoss || !takeProfit || !lotSize) {
    return null;
  }

  const entry = Number(entryPrice);
  const sl = Number(stopLoss);
  const tp = Number(takeProfit);
  const lots = Number(lotSize);

  if (entry === 0 || sl === 0 || tp === 0 || lots === 0) {
    return null;
  }

  // Determine trade direction based on SL position
  const isBuy = sl < entry;
  const tradeType = isBuy ? 'BUY' : 'SELL';

  // Calculate risk and reward using the same profit calculator
  const risk = Math.abs(calculateProfit(instrument, tradeType, entry, sl, lots));
  const reward = Math.abs(calculateProfit(instrument, tradeType, entry, tp, lots));

  if (risk === 0) return null;

  const ratio = reward / risk;
  // Format ratio: show integer if whole number, otherwise show up to 2 decimals without trailing zeros
  const formattedRatio = ratio % 1 === 0 ? ratio.toFixed(0) : ratio.toFixed(2).replace(/\.?0+$/, '');
  
  return {
    ratio: `1:${formattedRatio}`,
    risk: risk.toFixed(2),
    reward: reward.toFixed(2)
  };
};

const timeframes = [
  { value: '1', label: '1 Min' },
  { value: '5', label: '5 Min' },
  { value: '15', label: '15 Min' },
  { value: '30', label: '30 Min' },
  { value: '60', label: '1 Hr' },
  { value: '240', label: '4 Hr' },
  { value: 'D', label: '1 Day' },
  { value: 'M', label: '1 Month' },
];


const Trade = () => {
  // All useState declarations at the top
  const { user, getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState({ open: [], pending: [], closed: [] });
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(allSymbols[0].symbol);
  const [selectedTimeframe, setSelectedTimeframe] = useState('5');
  const [tab, setTab] = useState('open');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderType, setOrderType] = useState('market');
  const [pendingOrderType, setPendingOrderType] = useState('Buy Limit');
  const [pendingPrice, setPendingPrice] = useState('0.00000');
  const [pendingStopLimitPrice, setPendingStopLimitPrice] = useState('0.00000');
  const [expiration, setExpiration] = useState('GTC');
  const [expirationDate, setExpirationDate] = useState(new Date());
  const [orderSide, setOrderSide] = useState('buy');
  const [volume, setVolume] = useState('0.01');
  const [stopLoss, setStopLoss] = useState('0.00000');
  const [takeProfit, setTakeProfit] = useState('0.00000');
  const [livePrice, setLivePrice] = useState(null);
  const [priceIsCached, setPriceIsCached] = useState(false);
  const pollingRef = useRef(null);
  const [profitMap, setProfitMap] = useState({});
  const previousProfitMapRef = useRef({}); // Hold previous profit values as fallback
  const [horizontalLinePrice, setHorizontalLinePrice] = useState(null);
  const [mainPagePrice, setMainPagePrice] = useState(null);
  const mainPagePollingRef = useRef(null);
  
  // Market status state
  const [marketStatus, setMarketStatus] = useState({ open: true, message: '', tradingHours: '' });
  
  // Risk/Reward ratio state
  const [riskRewardRatio, setRiskRewardRatio] = useState(null);
  
  // Edit order states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editVolume, setEditVolume] = useState('');
  const [editStopLoss, setEditStopLoss] = useState('');
  const [editTakeProfit, setEditTakeProfit] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editRiskRewardRatio, setEditRiskRewardRatio] = useState(null);
  
  // Success dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Active challenge check state
  const [hasActiveChallenge, setHasActiveChallenge] = useState(null);
  const [checkingChallenge, setCheckingChallenge] = useState(true);
  
  // Challenge selection state
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loadingChallenges, setLoadingChallenges] = useState(false);

  // Real-time notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // WebSocket order closed event handler
  useEffect(() => {
    const handleOrderClosed = (data) => {
      console.log('📊 Order closed notification received:', data);
      
      // Show notification
      const profit = data.orderType === 'BUY' 
        ? (data.closePrice - data.entryPrice) * data.volume 
        : (data.entryPrice - data.closePrice) * data.volume;
      
      const profitSign = profit >= 0 ? '+' : '';
      const severity = data.reason.includes('Take Profit') ? 'success' : 'warning';
      
      setNotification({
        open: true,
        message: `Order #${data.orderId} closed: ${data.reason} at ${data.closePrice} (${profitSign}${profit.toFixed(2)} profit)`,
        severity: severity
      });
      
      // Refresh orders list
      fetchOrders();
    };
    
    // Register event handler
    websocketService.on('order-closed', handleOrderClosed);
    
    // Cleanup
    return () => {
      websocketService.off('order-closed', handleOrderClosed);
    };
  }, []);

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Fetch active challenges on mount
  useEffect(() => {
    const fetchActiveChallenges = async () => {
      setLoadingChallenges(true);
      try {
        // Use the user-with-progress endpoint to get phase information
        const response = await fetch(getApiUrl('/api/challenges/user-with-progress'), {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await response.json();
        if (data.success && data.challenges.length > 0) {
          // Filter only active challenges with XFUNDING platform
          const activeOnly = data.challenges.filter(c => 
            c.Status === 'Active' && c.Platform === 'XFUNDING'
          );
          setActiveChallenges(activeOnly);
          setSelectedChallenge(activeOnly[0]); // Auto-select first challenge
          setHasActiveChallenge(activeOnly.length > 0);
        } else {
          setActiveChallenges([]);
          setSelectedChallenge(null);
          setHasActiveChallenge(false);
        }
      } catch (error) {
        console.error('Error fetching active challenges:', error);
        setActiveChallenges([]);
        setSelectedChallenge(null);
        setHasActiveChallenge(false);
      } finally {
        setLoadingChallenges(false);
        setCheckingChallenge(false);
      }
    };

    if (user?.email) {
      fetchActiveChallenges();
    }
  }, [user]);

  // Check market status based on current symbol
  useEffect(() => {
    const checkMarketStatus = () => {
      const marketType = getMarketType(selectedSymbol);
      
      // Crypto is always open
      if (marketType === 'crypto') {
        setMarketStatus({ open: true, message: '', tradingHours: '' });
        return;
      }
      
      // Get current time in IST timezone
      const now = new Date();
      const istTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      
      const day = istTime.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      const hours = istTime.getHours();
      const minutes = istTime.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      
      // Saturday after 2:30 AM (150 minutes = 2:30)
      if (day === 6 && timeInMinutes >= 150) {
        setMarketStatus({ 
          open: false, 
          message: 'Market closes Saturday 2:30 AM IST',
          tradingHours: 'Trading Hours: Monday 3:30 AM - Saturday 2:30 AM IST'
        });
        return;
      }
      
      // All day Sunday
      if (day === 0) {
        setMarketStatus({ 
          open: false, 
          message: 'Market closed on Sundays',
          tradingHours: 'Trading Hours: Monday 3:30 AM - Saturday 2:30 AM IST'
        });
        return;
      }
      
      // Monday before 3:30 AM (210 minutes = 3:30)
      if (day === 1 && timeInMinutes < 210) {
        setMarketStatus({ 
          open: false, 
          message: 'Market opens Monday 3:30 AM IST',
          tradingHours: 'Trading Hours: Monday 3:30 AM - Saturday 2:30 AM IST'
        });
        return;
      }
      
      // Market is open
      setMarketStatus({ open: true, message: '', tradingHours: '' });
    };
    
    checkMarketStatus();
    // Check every minute
    const interval = setInterval(checkMarketStatus, 60000);
    return () => clearInterval(interval);
  }, [selectedSymbol]);

  // Poll price for open positions and update profit
  useEffect(() => {
    let interval;
    if (tab === 'open' && orders.open.length > 0) {
      const pollProfit = async () => {
        // Start with previous profit values (hold last known profit as fallback)
        const newProfitMap = { ...previousProfitMapRef.current };
        
        // Fetch prices for all unique symbols in open orders
        const uniqueSymbols = [...new Set(orders.open.map(order => order.Symbol))];
        
        await Promise.all(uniqueSymbols.map(async (symbol) => {
          let retries = 2; // Try twice if first attempt fails
          let currentPrice = null;
          
          while (retries > 0 && currentPrice === null) {
            try {
              const tvSymbol = symbol.startsWith('OANDA:') ? symbol : `OANDA:${symbol}`;
              const marketType = getMarketType(symbol);
              const res = await fetch(getApiUrl('/api/price'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbol: tvSymbol, market: marketType })
              });
              const data = await res.json();
              
              if (res.ok && data.price && !isNaN(data.price)) {
                currentPrice = Number(data.price);
              } else {
                throw new Error('Invalid price data');
              }
            } catch (e) {
              retries--;
              if (retries > 0) {
                // Wait 500ms before retry
                await new Promise(resolve => setTimeout(resolve, 500));
              } else {
                console.warn(`Could not fetch price for ${symbol}, holding last known profit value`);
                // Keep previous profit values - prevents showing $100 -> $0 panic
                // Next poll cycle will try again automatically
              }
            }
          }
          
          // Calculate profit for each order with this symbol (only if we got fresh price)
          if (currentPrice !== null) {
            orders.open.forEach(order => {
              if (order.Symbol === symbol) {
                const entryPrice = Number(order.Price);
                const volume = Number(order.Volume);
                const tradeType = order.InternalOrderType; // 'BUY' or 'SELL'
                
                // Use the comprehensive profit calculator
                const profitLoss = calculateProfit(
                  symbol,
                  tradeType,
                  entryPrice,
                  currentPrice,
                  volume
                );
                
                newProfitMap[order.OrderID] = profitLoss;
              }
            });
          }
        }));
        
        // Update state and ref for next cycle
        previousProfitMapRef.current = newProfitMap;
        setProfitMap(newProfitMap);
      };
      
      pollProfit();
      interval = setInterval(pollProfit, 5000); // Update every 5 seconds (reduced from 2s)
    } else {
      setProfitMap({});
      previousProfitMapRef.current = {};
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tab, orders.open, selectedSymbol]);

  // Poll price for main page display (next to symbol dropdown)
  useEffect(() => {
    // Reset price and cached status when symbol changes
    setMainPagePrice(null);
    setPriceIsCached(false);
    
    const fetchMainPagePrice = async () => {
      const tvSymbol = selectedSymbol.startsWith('OANDA:') ? selectedSymbol : `OANDA:${selectedSymbol}`;
      const marketType = getMarketType(selectedSymbol);
      try {
        const res = await fetch(getApiUrl('/api/price'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol: tvSymbol, market: marketType })
        });
        const data = await res.json();
        if (res.ok && data.price && !isNaN(data.price)) {
          setMainPagePrice(data.price);
          setPriceIsCached(data.cached === true);
        } else {
          // Only set to 'error' if we don't have a previous price
          if (mainPagePrice === null) {
            setMainPagePrice('error');
            setPriceIsCached(false);
          }
          // If we already have a price, keep it (stale price is better than no price)
        }
      } catch (e) {
        // On error, keep the last known price
        if (mainPagePrice === null) {
          setMainPagePrice('error');
          setPriceIsCached(false);
        }
      }
    };

    fetchMainPagePrice();
    mainPagePollingRef.current = setInterval(fetchMainPagePrice, 1000);

    return () => {
      if (mainPagePollingRef.current) clearInterval(mainPagePollingRef.current);
    };
  }, [selectedSymbol]);

  // Calculate Risk/Reward ratio when SL/TP/Volume change
  useEffect(() => {
    if (orderType === 'market' && mainPagePrice) {
      const ratio = calculateRiskReward(selectedSymbol, mainPagePrice, stopLoss, takeProfit, volume);
      setRiskRewardRatio(ratio);
    } else if (orderType === 'pending' && pendingPrice) {
      const ratio = calculateRiskReward(selectedSymbol, pendingPrice, stopLoss, takeProfit, volume);
      setRiskRewardRatio(ratio);
    } else {
      setRiskRewardRatio(null);
    }
  }, [orderType, mainPagePrice, pendingPrice, stopLoss, takeProfit, volume, selectedSymbol]);

  // Calculate Risk/Reward ratio for edit modal
  useEffect(() => {
    if (editingOrder) {
      const entryPrice = editingOrder.Status === 'PENDING' ? editPrice : editingOrder.Price;
      const vol = editingOrder.Status === 'PENDING' ? editVolume : editingOrder.Volume;
      const ratio = calculateRiskReward(editingOrder.Symbol, entryPrice, editStopLoss, editTakeProfit, vol);
      setEditRiskRewardRatio(ratio);
    }
  }, [editingOrder, editPrice, editVolume, editStopLoss, editTakeProfit]);

  // Check and execute pending orders automatically
  useEffect(() => {
    if (!user?.email) return;
    
    const checkPendingOrders = async () => {
      console.log('🔄 Checking pending orders for:', user.email, 'at', new Date().toLocaleTimeString());
      const authHeaders = getAuthHeaders();
      console.log('🔑 Auth headers:', authHeaders);
      try {
        const res = await fetch(getApiUrl('/api/orders/check-pending'), {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ email: user.email })
        });
        const data = await res.json();
        console.log('✅ Pending orders check response:', data);
        
        // Handle authentication errors
        if (res.status === 401 || res.status === 403) {
          alert('Session expired. Please login again.');
          logout();
          navigate('/signin');
          return;
        }
        
        if (res.ok && (data.executed > 0 || data.closed > 0)) {
          console.log(`🎯 Executed ${data.executed} pending orders, Closed ${data.closed} open orders`);
          // Refresh OPEN, PENDING, and CLOSED orders
          await fetchOrders('OPEN');
          await fetchOrders('PENDING');
          await fetchOrders('CLOSED');
          console.log('📊 Refreshed all orders lists');
        } else if (res.ok) {
          console.log(`ℹ️ No orders executed/closed. Checked ${data.total} orders.`);
        }
      } catch (err) {
        console.error('❌ Error checking pending orders:', err);
      }
    };

    // Check immediately
    console.log('🚀 Starting pending order monitoring for', user.email);
    checkPendingOrders();
    
    // Then check every 5 seconds
    const interval = setInterval(checkPendingOrders, 5000);
    
    return () => {
      console.log('🛑 Stopping pending order monitoring');
      clearInterval(interval);
    };
  }, [user]);

  // Close order handler
  const handleCloseOrder = async (orderId) => {
    try {
      // Find the order to get its symbol and status
      const order = [...orders.open, ...orders.pending].find(o => o.OrderID === orderId);
      if (!order) {
        alert('Order not found.');
        return;
      }

      const isPendingOrder = orders.pending.some(o => o.OrderID === orderId);

      // For PENDING orders, use cancel endpoint (no P/L calculation)
      if (isPendingOrder) {
        console.log(`Cancelling pending order ${orderId}`);
        
        const res = await fetch(getApiUrl('/api/orders/cancel'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ orderId })
        });
        const data = await res.json();
        
        if (res.ok && data.success) {
          console.log(`✅ Pending order cancelled successfully`);
          setSuccessMessage('Pending order cancelled successfully!');
          setShowSuccessDialog(true);
          fetchOrders(tab.toUpperCase());
        } else {
          alert(data.error || 'Failed to cancel order.');
        }
        return;
      }

      // For OPEN orders, fetch market price and close with P/L
      const marketType = getMarketType(order.Symbol);
      const priceRes = await fetch(getApiUrl('/api/price'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: order.Symbol, market: marketType })
      });
      const priceData = await priceRes.json();
      
      if (!priceData.price) {
        alert('Could not fetch current market price. Please try again.');
        return;
      }

      const exitPrice = priceData.price;
      console.log(`Closing order ${orderId} at exit price: ${exitPrice}`);

      // Close the order with exit price
      const res = await fetch(getApiUrl('/api/orders/close'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderId, exitPrice })
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        console.log(`✅ Order closed. P/L: $${data.data.ProfitLoss}, Pips: ${data.data.PipsGainLoss}`);
        setSuccessMessage('Order closed successfully!');
        setShowSuccessDialog(true);
        fetchOrders(tab.toUpperCase());
      } else {
        alert(data.error || 'Failed to close order.');
      }
    } catch (err) {
      console.error('Close order error:', err);
      alert('Failed to close order.');
    }
  };

  // Handle edit order - open modal with current values
  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setEditVolume(order.Volume || '');
    setEditStopLoss(order.StopLoss || '');
    setEditTakeProfit(order.TakeProfit || '');
    setEditPrice(order.Price || '');
    setShowEditModal(true);
  };

  // Handle update order
  const handleUpdateOrder = async () => {
    if (!editingOrder) return;
    
    try {
      const updateData = {
        orderId: editingOrder.OrderID,
        stopLoss: parseFloat(editStopLoss),
        takeProfit: parseFloat(editTakeProfit),
        price: parseFloat(editPrice)
      };
      
      // Only include volume for PENDING orders (users can't change volume for OPEN orders)
      if (editingOrder.Status === 'PENDING') {
        updateData.volume = parseFloat(editVolume);
      }
      
      console.log('📝 Sending update request:', updateData);
      
      const res = await fetch(getApiUrl('/api/orders/update'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });
      
      const data = await res.json();
      console.log('📝 Update response:', data);
      
      // Handle authentication errors
      if (res.status === 401 || res.status === 403) {
        // Check if it's a market closed error
        if (data.error === 'Market is closed') {
          alert(`${data.message}\n\n${data.tradingHours || ''}`);
        } else {
          alert('Session expired. Please login again.');
          logout();
          navigate('/signin');
        }
        return;
      }
      
      if (res.ok && data.success) {
        setSuccessMessage('Order updated successfully!');
        setShowSuccessDialog(true);
        setShowEditModal(false);
        setEditingOrder(null);
        // Refresh orders
        await fetchOrders(tab.toUpperCase());
      } else {
        alert(data.error || 'Failed to update order.');
      }
    } catch (err) {
      console.error('Update order error:', err);
      alert('Failed to update order.');
    }
  };
  // All useState declarations at the top

  // Fetch orders by status
  const fetchOrders = async (status) => {
    if (!user?.email || !selectedChallenge) return;
    console.log(`📥 Fetching ${status} orders for ${user.email} - Challenge ${selectedChallenge.ChallengeID}`);
    setLoadingOrders(true);
    try {
      const res = await fetch(getApiUrl('/api/orders'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          email: user.email, 
          status,
          challengeId: selectedChallenge.ChallengeID
        })
      });
      const data = await res.json();
      console.log(`📦 Received ${status} orders response:`, data);
      console.log(`   Response status: ${res.status}, OK: ${res.ok}`);
      
      if (res.status === 401 || res.status === 403) {
        console.log('❌ Authentication failed. Please login again.');
        alert('Session expired. Please login again.');
        return;
      }
      
      if (res.ok && data.orders) {
        console.log(`✅ Setting ${data.orders.length} ${status} orders`);
        setOrders(prev => ({ ...prev, [status.toLowerCase()]: data.orders }));
      } else {
        console.log(`❌ Failed to fetch ${status} orders:`, data.error || data);
      }
    } catch (err) {
      console.error(`❌ Error fetching ${status} orders:`, err);
    }
    setLoadingOrders(false);
  };

  // Fetch orders when tab changes or after order creation or challenge changes
  useEffect(() => {
    if (selectedChallenge) {
      fetchOrders(tab.toUpperCase());
    }
    // eslint-disable-next-line
  }, [tab, user, selectedChallenge]);

  // Order creation handler
  const handleConfirmOrder = async () => {
    // Determine values based on business rules
    const isMarketExecution = orderType === 'market';
    const executionType = isMarketExecution ? 'Market Execution' : 'Pending Order';
    const internalOrderType = orderSide === 'buy' ? 'BUY' : 'SELL';
    
    // Fetch current market price (needed for both market orders AND pending order type calculation)
    let currentMarketPrice = parseFloat(livePrice);
    
    // For market orders, use current market price as the execution price
    let priceValue = isMarketExecution ? livePrice : pendingPrice;
    
    // If current market price is not available, fetch it
    if (isNaN(currentMarketPrice) || !currentMarketPrice || currentMarketPrice === 0 || currentMarketPrice === 'error') {
      console.log('🔄 Fetching current market price...');
      try {
        const tvSymbol = selectedSymbol.startsWith('OANDA:') ? selectedSymbol : `OANDA:${selectedSymbol}`;
        const marketType = getMarketType(selectedSymbol);
        const res = await fetch(getApiUrl('/api/price'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol: tvSymbol, market: marketType })
        });
        const data = await res.json();
        if (res.ok && data.price && !isNaN(data.price)) {
          currentMarketPrice = parseFloat(data.price);
          console.log('✅ Fetched current market price:', currentMarketPrice);
          
          // If it's a market order, use this fetched price as execution price
          if (isMarketExecution) {
            priceValue = currentMarketPrice;
          }
        } else {
          alert('Unable to fetch current market price. Please try again.');
          return;
        }
      } catch (e) {
        alert('Unable to fetch current market price. Please try again.');
        return;
      }
    } else {
      console.log('✅ Using live price:', currentMarketPrice);
    }
    
    if (!priceValue || priceValue === 'error' || isNaN(priceValue)) {
      alert('Price unavailable. Cannot create order.');
      return;
    }
    
    // 🎯 AUTO-CALCULATE ORDER TYPE for Pending Orders (Smart Logic)
    let orderTypeValue = null;
    if (!isMarketExecution) {
      const userEnteredPrice = parseFloat(priceValue);
      
      if (!isNaN(currentMarketPrice) && !isNaN(userEnteredPrice) && currentMarketPrice > 0) {
        if (internalOrderType === 'BUY') {
          // BUY LOGIC:
          // - If price BELOW market → Buy Limit (wait for price to drop)
          // - If price ABOVE market → Buy Stop (wait for breakout)
          orderTypeValue = userEnteredPrice < currentMarketPrice ? 'Buy Limit' : 'Buy Stop';
        } else if (internalOrderType === 'SELL') {
          // SELL LOGIC:
          // - If price ABOVE market → Sell Limit (wait for price to rise)
          // - If price BELOW market → Sell Stop (wait for breakdown)
          orderTypeValue = userEnteredPrice > currentMarketPrice ? 'Sell Limit' : 'Sell Stop';
        }
        
        console.log('🎯 AUTO-CALCULATED ORDER TYPE:');
        console.log(`   Market Price: ${currentMarketPrice}`);
        console.log(`   Entered Price: ${userEnteredPrice}`);
        console.log(`   Action: ${internalOrderType}`);
        console.log(`   ✅ Order Type: ${orderTypeValue}`);
      } else {
        console.error('❌ Order type calculation failed:');
        console.error(`   currentMarketPrice: ${currentMarketPrice} (valid: ${!isNaN(currentMarketPrice) && currentMarketPrice > 0})`);
        console.error(`   userEnteredPrice: ${userEnteredPrice} (valid: ${!isNaN(userEnteredPrice)})`);
        alert(`Unable to determine order type.\n\nMarket Price: ${currentMarketPrice || 'unavailable'}\nYour Price: ${userEnteredPrice || 'unavailable'}`);
        return;
      }
    }
    
    const stopLimitPriceValue = isMarketExecution ? null : pendingStopLimitPrice;
    const expirationValue = isMarketExecution ? null : expiration;
    let expirationDateValue = null;
    if (!isMarketExecution && expiration === 'Today') {
      const now = new Date();
      expirationDateValue = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    } else if (!isMarketExecution && expiration === 'Specific Day') {
      expirationDateValue = expirationDate;
    }
    try {
      // Validate challenge selection
      if (!selectedChallenge) {
        alert('Please select a challenge to trade with.');
        return;
      }
      
      // Block order placement if challenge is Failed
      if (selectedChallenge.CurrentPhase === 'Failed') {
        alert('Cannot place orders on a failed challenge. Please select a different challenge.');
        return;
      }

      const res = await fetch(getApiUrl('/api/order'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: user?.email,
          challengeId: selectedChallenge.ChallengeID,
          symbol: selectedSymbol,
          executionType,
          orderType: orderTypeValue,
          internalOrderType,
          price: priceValue,
          stopLimitPrice: stopLimitPriceValue,
          expiration: expirationValue,
          expirationDate: expirationDateValue,
          volume,
          stopLoss,
          takeProfit
        })
      });
      const data = await res.json();
      
      // Handle authentication errors
      if (res.status === 401 || res.status === 403) {
        // Check if it's a market closed error
        if (data.error === 'Market is closed') {
          alert(`${data.message}\n\n${data.tradingHours || ''}`);
        } else {
          alert('Session expired. Please login again.');
          logout();
          navigate('/signin');
        }
        return;
      }
      
      if (res.ok && data.success) {
        setSuccessMessage('Order created successfully!');
        setShowSuccessDialog(true);
        setShowOrderModal(false);
        
        // Reset all form fields to default values
        setVolume('0.01');
        setStopLoss('0.00000');
        setTakeProfit('0.00000');
        setPendingPrice('0.00000');
        setPendingStopLimitPrice('0.00000');
        setOrderType('market');
        // setPendingOrderType('Buy Limit'); // REMOVED: No longer needed, auto-calculated
        setExpiration('GTC');
        setExpirationDate(new Date());
        setOrderSide('buy');
        
        // Refresh orders to show the new order
        await fetchOrders(orderType === 'market' ? 'OPEN' : 'PENDING');
      } else {
        alert(data.error || 'Failed to create order.');
      }
    } catch (err) {
      alert('Failed to create order.');
    }
  };

  // Helper to fetch price from backend proxy (TradingView scan endpoint)
  const fetchPrice = async (symbol) => {
    // For forex, TradingView expects symbols like 'OANDA:EURUSD', market 'forex'
    const tvSymbol = symbol.startsWith('OANDA:') ? symbol : `OANDA:${symbol}`;
    const marketType = getMarketType(symbol);
    try {
      const res = await fetch(getApiUrl('/api/price'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: tvSymbol, market: marketType })
      });
      const data = await res.json();
      if (res.ok && data.price && !isNaN(data.price)) {
        setLivePrice(data.price);
        setPriceIsCached(data.cached === true);
      } else {
        // Only set to 'error' if we don't have a previous price
        if (livePrice === null) {
          setLivePrice('error');
          setPriceIsCached(false);
        }
        // If we already have a price, keep it (stale price is better than no price)
      }
    } catch (e) {
      // On error, keep the last known price
      if (livePrice === null) {
        setLivePrice('error');
        setPriceIsCached(false);
      }
    }
  };

  // Poll price when modal is open
  useEffect(() => {
    if (showOrderModal) {
      fetchPrice(selectedSymbol);
      pollingRef.current = setInterval(() => fetchPrice(selectedSymbol), 1000);
    } else {
      setLivePrice(null);
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line
  }, [showOrderModal, selectedSymbol]);

  // Handler for when user places horizontal line
  const handleHorizontalLinePrice = (price) => {
    setHorizontalLinePrice(price);
    console.log('Horizontal line placed at price:', price);
    
    // You can auto-fill order forms with this price
    if (showOrderModal) {
      if (orderType === 'pending') {
        setPendingPrice(price.toFixed(5));
      }
    }
  };

  let message = '';
  if (tab === 'open' && orders.open.length === 0) message = "You don't have any open positions because you are yet to trade.";
  if (tab === 'pending' && orders.pending.length === 0) message = "You don't have any pending orders because you are yet to trade.";
  if (tab === 'closed' && orders.closed.length === 0) message = "You don't have any closed positions yet.";

  // Show loading while checking challenge status
  if (checkingChallenge) {
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#131722'
      }}>
        <Typography sx={{ color: '#d1d4dc', fontSize: 16 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show message if user doesn't have an active challenge
  if (hasActiveChallenge === false) {
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#131722',
        flexDirection: 'column',
        gap: 3
      }}>
        <Box sx={{
          bgcolor: '#1e222d',
          borderRadius: 2,
          p: 4,
          maxWidth: 500,
          textAlign: 'center',
          border: '1px solid #2a2e39'
        }}>
          <Typography sx={{ 
            color: '#fff', 
            fontSize: 24, 
            fontWeight: 600,
            mb: 2
          }}>
            Active Challenge Required
          </Typography>
          <Typography sx={{ 
            color: '#d1d4dc', 
            fontSize: 15,
            lineHeight: 1.7,
            mb: 3
          }}>
            You need an active <strong>XFUNDING Platform</strong> challenge to access the trading page. 
            Purchase a challenge with XFUNDING platform and wait for activation to start trading.
          </Typography>
          <Typography sx={{ 
            color: '#f59e0b', 
            fontSize: 13,
            lineHeight: 1.6,
            mb: 3,
            bgcolor: 'rgba(245, 158, 11, 0.1)',
            p: 2,
            borderRadius: 1,
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}>
            💡 Note: MT5 Platform challenges do not have access to this trading interface. Please select XFUNDING Platform when purchasing a challenge.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/trader-summary')}
            sx={{
              bgcolor: '#4ade80',
              color: '#fff',
              px: 4,
              py: 1.5,
              fontSize: 15,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
              '&:hover': {
                bgcolor: '#22c55e',
                boxShadow: '0 6px 16px rgba(74, 222, 128, 0.4)'
              }
            }}
          >
            Buy Challenge
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', gap: 0, overflow: 'hidden', mt: '-40px', ml: '-30px', mr: '-40px' }}>
      {/* Left Side - Chart Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Symbol and Price Header */}
        <Box sx={{ pt: '5px', pb: 1, px: 2, borderBottom: '1px solid #2a2e39', display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#131722' }}>
          {/* Challenge Selector */}
          <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#1e222d', '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#fff' } }}>
            <InputLabel sx={{ color: '#fff', fontSize: 14 }}>Challenge</InputLabel>
            <Select
              value={selectedChallenge ? selectedChallenge.ChallengeID : ''}
              onChange={e => {
                const challenge = activeChallenges.find(c => c.ChallengeID === e.target.value);
                setSelectedChallenge(challenge);
              }}
              label="Challenge"
              disabled={loadingChallenges || activeChallenges.length === 0}
              sx={{ 
                color: '#fff',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a3e49' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4a4e59' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5a5e69' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    bgcolor: '#1e222d',
                    '& .MuiMenuItem-root': {
                      color: '#d1d4dc',
                      fontSize: 13,
                      '&:hover': { bgcolor: '#2a2e39' },
                      '&.Mui-selected': { bgcolor: '#2a2e39' }
                    }
                  }
                }
              }}
            >
              {activeChallenges.map(challenge => {
                const phaseBadge = getPhaseBadge(challenge.CurrentPhase);
                const isBlocked = challenge.CurrentPhase === 'Failed';
                
                return (
                  <MenuItem 
                    key={challenge.ChallengeID} 
                    value={challenge.ChallengeID}
                    disabled={isBlocked}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      opacity: isBlocked ? 0.5 : 1
                    }}
                  >
                    <span>
                      {challenge.CustomName || `${challenge.ChallengeType} - $${challenge.ChallengeAmount?.toLocaleString()}`}
                    </span>
                    <Chip
                      label={phaseBadge.label}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        fontSize: '10px',
                        fontWeight: 600,
                        color: phaseBadge.color,
                        bgcolor: phaseBadge.bgColor,
                        border: `1px solid ${phaseBadge.color}`,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Symbol Selector */}
          <FormControl size="small" sx={{ minWidth: 200, bgcolor: '#1e222d', '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#fff' } }}>
            <Select
              value={selectedSymbol}
              onChange={e => setSelectedSymbol(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    bgcolor: '#1e222d',
                    '& .MuiList-root': {
                      paddingTop: 0,
                      paddingBottom: 0
                    },
                    '&::-webkit-scrollbar': {
                      width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: '#131722'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: '#3a3e49',
                      borderRadius: '4px',
                      '&:hover': {
                        bgcolor: '#4a4e59'
                      }
                    }
                  }
                }
              }}
              sx={{ 
                color: '#fff',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a3e49' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4a4e59' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5a5e69' }
              }}
            >
              {tradingSymbols.map((group, groupIndex) => [
                <ListSubheader 
                  key={`header-${groupIndex}`}
                  sx={{ 
                    bgcolor: '#2a2e39', 
                    color: '#888', 
                    fontWeight: 600,
                    fontSize: '11px',
                    lineHeight: '32px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {group.category}
                </ListSubheader>,
                ...group.symbols.map(symbol => (
                  <MenuItem 
                    key={symbol.symbol} 
                    value={symbol.symbol}
                    sx={{
                      fontSize: '13px',
                      pl: 3,
                      '&:hover': { bgcolor: '#1e222d' }
                    }}
                  >
                    {symbol.name}
                  </MenuItem>
                ))
              ])}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, bgcolor: '#1e222d', '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#fff' } }}>
            <Select
              value={selectedTimeframe}
              onChange={e => setSelectedTimeframe(e.target.value)}
              sx={{ 
                color: '#fff',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a3e49' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4a4e59' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5a5e69' }
              }}
            >
              {timeframes.map(tf => (
                <MenuItem key={tf.value} value={tf.value}>{tf.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Price display next to timeframe - commented out for later use */}
          {/* {mainPagePrice !== null && mainPagePrice !== 'error' && (
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2196f3' }}>
              {Number(mainPagePrice).toFixed(5)}
            </Typography>
          )}
          {mainPagePrice === null && (
            <Typography variant="body2" sx={{ color: '#888' }}>Loading...</Typography>
          )} */}
        </Box>
        
        {/* Chart */}
        <Box sx={{ height: 'calc(100vh - 200px)', overflow: 'hidden', pb: 1 }}>
          <TradingViewWidget 
            symbol={selectedSymbol} 
            interval={selectedTimeframe}
            onHorizontalLinePrice={handleHorizontalLinePrice}
          />
        </Box>

        {/* Orders Table Tabs at Bottom */}
        <Box sx={{ borderTop: '1px solid #2a2e39', bgcolor: '#131722' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 11 }, '& .Mui-selected': { color: '#fff !important' } }}
          >
            <Tab value="open" label="Open" />
            <Tab value="pending" label="Pending" />
            <Tab value="closed" label="Closed" />
          </Tabs>
          <Box sx={{ maxHeight: 180, overflowY: 'auto', bgcolor: '#131722' }}>
            {loadingOrders ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : orders[tab].length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#d1d4dc' }}>
                <thead>
                  <tr style={{ background: '#1e222d', fontWeight: 600, color: '#787b86' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Symbol</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Price</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Volume</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>SL</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>TP</th>
                    {tab === 'closed' && <th style={{ padding: '6px 8px', textAlign: 'left' }}>Exit Price</th>}
                    {(tab === 'open' || tab === 'closed') && <th style={{ padding: '6px 8px', textAlign: 'left' }}>Profit</th>}
                    {(tab === 'open' || tab === 'pending') && <th style={{ padding: '6px 8px', textAlign: 'left' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {orders[tab].map((order, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #2a2e39', color: '#d1d4dc' }}>
                      <td style={{ padding: '6px 8px' }}>{order.Symbol}</td>
                      <td style={{ padding: '6px 8px' }}>{order.InternalOrderType}</td>
                      <td style={{ padding: '6px 8px' }}>{order.Price}</td>
                      <td style={{ padding: '6px 8px' }}>{order.Volume}</td>
                      <td style={{ padding: '6px 8px' }}>{order.StopLoss}</td>
                      <td style={{ padding: '6px 8px' }}>{order.TakeProfit}</td>
                      {tab === 'closed' && (
                        <td style={{ padding: '6px 8px' }}>
                          {order.ExitPrice || '--'}
                        </td>
                      )}
                      {tab === 'open' && (
                        <td style={{ 
                          padding: '6px 8px', 
                          color: profitMap[order.OrderID] >= 0 ? '#10b981' : '#ef4444', 
                          fontWeight: 600 
                        }}>
                          {profitMap[order.OrderID] !== undefined ? profitMap[order.OrderID].toFixed(2) : '--'}
                        </td>
                      )}
                      {tab === 'closed' && (
                        <td style={{ 
                          padding: '6px 8px', 
                          color: order.ActualProfitLoss >= 0 ? '#10b981' : '#ef4444', 
                          fontWeight: 600 
                        }}>
                          {order.ActualProfitLoss != null ? Number(order.ActualProfitLoss).toFixed(2) : '--'}
                        </td>
                      )}
                      {(tab === 'open' || tab === 'pending') && (
                        <td style={{ padding: '6px 8px', display: 'flex', gap: '4px' }}>
                          <Button size="small" onClick={() => handleEditOrder(order)} sx={{ minWidth: 20, p: 0 }}>
                            <EditIcon sx={{ color: '#3b82f6', fontSize: 18, '&:hover': { color: '#60a5fa' } }} />
                          </Button>
                          <Button size="small" onClick={() => handleCloseOrder(order.OrderID)} sx={{ minWidth: 20, p: 0 }}>
                            <CloseIcon sx={{ color: '#787b86', fontSize: 20, '&:hover': { color: '#fff' } }} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center', color: '#888', fontSize: 14 }}>{message}</Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right Side - Trading Panel */}
      <Box sx={{ 
        width: 380, 
        bgcolor: '#131722', 
        display: 'flex', 
        flexDirection: 'column',
        borderLeft: '1px solid #2a2e39',
        overflow: 'hidden'
      }}>
        {/* Symbol Header */}
        <Box sx={{ py: 1, px: 2, borderBottom: '1px solid #2a2e39', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#3a3e49', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#888', fontSize: 12 }}>
              {selectedSymbol.substring(0, 1)}
            </Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>
              {selectedSymbol}
            </Typography>
          </Box>
          <Button size="small" onClick={() => setShowOrderModal(false)} sx={{ color: '#787b86', minWidth: 30, '&:hover': { color: '#fff' } }}>
            ✕
          </Button>
        </Box>

        {/* Market Status Banner */}
        {!marketStatus.open && (
          <Box sx={{ 
            mx: 2, 
            mt: 1.5,
            mb: 1,
            p: 1.5, 
            bgcolor: '#3a1f1f', 
            borderRadius: 1,
            border: '1px solid #6b2626'
          }}>
            <Typography sx={{ color: '#ef4444', fontSize: 12, fontWeight: 600, mb: 0.5 }}>
              ⚠ MARKET CLOSED
            </Typography>
            <Typography sx={{ color: '#d1d4dc', fontSize: 11, mb: 0.5 }}>
              {marketStatus.message}
            </Typography>
            <Typography sx={{ color: '#888', fontSize: 10 }}>
              {marketStatus.tradingHours}
            </Typography>
          </Box>
        )}

        {/* Live Price Display */}
        {/* Current Price Box - Hidden from user view */}
        {/* {mainPagePrice !== null && mainPagePrice !== 'error' && (
          <Box sx={{ 
            mx: 2, 
            mt: marketStatus.open ? 1.5 : 1,
            mb: 1,
            p: 1.5, 
            bgcolor: '#1e222d', 
            borderRadius: 1,
            border: priceIsCached ? '1px solid #f59e0b' : '1px solid #2a2e39',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box>
              <Typography sx={{ color: '#888', fontSize: 10, mb: 0.3 }}>
                Current Price
              </Typography>
              <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
                {Number(mainPagePrice).toFixed(5)}
              </Typography>
            </Box>
            {priceIsCached && (
              <Box sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: 'rgba(245, 158, 11, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <Typography sx={{ color: '#f59e0b', fontSize: 9, fontWeight: 600 }}>
                  CACHED
                </Typography>
              </Box>
            )}
          </Box>
        )} */}

        {/* Regular Form / Execution Type Dropdown */}
        <Box sx={{ py: 1, px: 2, overflowY: 'auto', flex: 1 }}>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <Select
              value={orderType}
              onChange={e => setOrderType(e.target.value)}
              sx={{ 
                bgcolor: '#1e222d', 
                color: '#fff',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' }
              }}
            >
              <MenuItem value="market">Market</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* Sell/Buy Buttons - UPDATED VERSION */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button
              fullWidth
              variant={orderSide === 'sell' ? 'contained' : 'outlined'}
              onClick={() => setOrderSide('sell')}
              disabled={!marketStatus.open}
              sx={{
                py: 1.5,
                background: orderSide === 'sell' ? 'linear-gradient(135deg, #c8384f 0%, #a82d40 100%)' : 'transparent',
                color: orderSide === 'sell' ? '#fff' : '#c8384f',
                borderColor: orderSide === 'sell' ? '#c8384f' : '#2a2e39',
                fontWeight: 600,
                fontSize: 12,
                '&:hover': { 
                  background: orderSide === 'sell' ? 'linear-gradient(135deg, #b12f43 0%, #8f2436 100%)' : 'rgba(200,56,79,0.08)',
                  borderColor: '#c8384f'
                },
                '&.Mui-disabled': {
                  background: '#2a2e39',
                  color: '#555',
                  borderColor: '#2a2e39'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Sell</Typography>
                {/* Price display in Sell button - commented out for future use */}
                {/* {mainPagePrice !== null && mainPagePrice !== 'error' && (
                  <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 0.5 }}>
                    {Number(mainPagePrice).toFixed(5)}
                  </Typography>
                )} */}
              </Box>
            </Button>
            <Button
              fullWidth
              variant={orderSide === 'buy' ? 'contained' : 'outlined'}
              onClick={() => setOrderSide('buy')}
              disabled={!marketStatus.open}
              sx={{
                py: 1.5,
                background: orderSide === 'buy' ? 'linear-gradient(135deg, #162447 0%, #1f4068 100%)' : 'transparent',
                color: orderSide === 'buy' ? '#fff' : '#3b82f6',
                borderColor: orderSide === 'buy' ? '#1f4068' : '#2a2e39',
                fontWeight: 600,
                fontSize: 12,
                '&:hover': { 
                  background: orderSide === 'buy' ? 'linear-gradient(135deg, #1f4068 0%, #162447 100%)' : 'rgba(59,130,246,0.08)',
                  borderColor: '#1f4068'
                },
                '&.Mui-disabled': {
                  background: '#2a2e39',
                  color: '#555',
                  borderColor: '#2a2e39'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Buy</Typography>
                {/* Price display in Buy button - commented out for future use */}
                {/* {mainPagePrice !== null && mainPagePrice !== 'error' && (
                  <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 0.5 }}>
                    {(Number(mainPagePrice) * 1.0001).toFixed(5)}
                  </Typography>
                )} */}
              </Box>
            </Button>
          </Box>

          {/* Order Type (for pending orders) - HIDDEN: Auto-calculated based on price vs market */}
          {/* 
          {orderType === 'pending' && (
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5 }}>Order Type</Typography>
              <Select
                value={pendingOrderType}
                onChange={e => setPendingOrderType(e.target.value)}
                sx={{ 
                  bgcolor: '#1e222d', 
                  color: '#fff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' }
                }}
              >
                <MenuItem value="Buy Limit">Buy Limit</MenuItem>
                <MenuItem value="Sell Limit">Sell Limit</MenuItem>
                <MenuItem value="Buy Stop">Buy Stop</MenuItem>
                <MenuItem value="Sell Stop">Sell Stop</MenuItem>
                <MenuItem value="Buy Stop Limit">Buy Stop Limit</MenuItem>
                <MenuItem value="Sell Stop Limit">Sell Stop Limit</MenuItem>
              </Select>
            </FormControl>
          )}
          */}

          {/* Price (for pending orders) */}
          {orderType === 'pending' && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5 }}>Price</Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={pendingPrice}
                onChange={e => setPendingPrice(e.target.value)}
                inputProps={{ step: 0.00001 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1e222d',
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' }
                  }
                }}
              />
            </Box>
          )}

          {/* Volume */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5 }}>Volume</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setVolume(String(Math.max(0.01, parseFloat(volume) - 0.01)))}
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                −
              </Button>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={volume}
                onChange={e => setVolume(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1e222d',
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    textAlign: 'center'
                  },
                  '& input': { textAlign: 'center' }
                }}
              />
              <Typography sx={{ color: '#888', fontSize: 12, minWidth: 40 }}>Lots</Typography>
              <Button
                size="small"
                onClick={() => setVolume(String(parseFloat(volume) + 0.01))}
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                +
              </Button>
            </Box>
          </Box>

          {/* Take Profit */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5 }}>Take Profit</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="Not set"
                value={takeProfit}
                onChange={e => setTakeProfit(e.target.value)}
                inputProps={{ step: 0.00001 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1e222d',
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' }
                  }
                }}
              />
              <Typography sx={{ color: '#888', fontSize: 12, minWidth: 40 }}>Price</Typography>
              <Button
                size="small"
                onClick={() => setTakeProfit('')}
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                −
              </Button>
              <Button
                size="small"
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                +
              </Button>
            </Box>
          </Box>

          {/* Stop Loss */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5 }}>Stop Loss</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="Not set"
                value={stopLoss}
                onChange={e => setStopLoss(e.target.value)}
                inputProps={{ step: 0.00001 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1e222d',
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' }
                  }
                }}
              />
              <Typography sx={{ color: '#888', fontSize: 12, minWidth: 40 }}>Price</Typography>
              <Button
                size="small"
                onClick={() => setStopLoss('')}
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                −
              </Button>
              <Button
                size="small"
                sx={{ minWidth: 36, bgcolor: '#1e222d', color: '#d1d4dc', border: '1px solid #2a2e39', '&:hover': { bgcolor: '#2a2e39', color: '#fff' } }}
              >
                +
              </Button>
            </Box>
          </Box>

          {/* Risk/Reward Ratio Display */}
          {riskRewardRatio && (
            <Box sx={{ mb: 1.5, textAlign: 'center', px: 2 }}>
              <Typography sx={{ color: '#4ade80', fontSize: 11, fontWeight: 600, mb: 0.3 }}>
                Risk:Reward {riskRewardRatio.ratio}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: 9, lineHeight: 1.4 }}>
                Potential Gain: ${riskRewardRatio.reward} & Max Loss: ${riskRewardRatio.risk}
              </Typography>
            </Box>
          )}

          {/* Place Order Button - UPDATED */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmOrder}
            disabled={!marketStatus.open}
            sx={{
              py: 1.2,
              background: orderSide === 'buy' ? 'linear-gradient(135deg, #162447 0%, #1f4068 100%)' : 'linear-gradient(135deg, #c8384f 0%, #a82d40 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              textTransform: 'none',
              '&:hover': {
                background: orderSide === 'buy' ? 'linear-gradient(135deg, #1f4068 0%, #162447 100%)' : 'linear-gradient(135deg, #b12f43 0%, #8f2436 100%)'
              },
              '&.Mui-disabled': {
                background: '#2a2e39',
                color: '#555'
              }
            }}
          >
            {orderSide === 'buy' ? 'Buy' : 'Sell'} {selectedSymbol}
          </Button>
        </Box>
      </Box>

      {/* Keep old modal hidden */}
  <Dialog open={false} onClose={() => setShowOrderModal(false)}>
      </Dialog>

      {/* Edit Order Modal */}
      <Dialog 
        open={showEditModal} 
        onClose={() => setShowEditModal(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1e222d',
            borderRadius: 2,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', bgcolor: '#131722', borderBottom: '1px solid #2a2e39', pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Order - {editingOrder?.Symbol}
            </Typography>
            <Button size="small" onClick={() => setShowEditModal(false)} sx={{ color: '#787b86', minWidth: 30, '&:hover': { color: '#fff' } }}>
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" sx={{ color: '#888', mb: 1 }}>
              Order Type: <span style={{ color: '#fff', fontWeight: 600 }}>{editingOrder?.InternalOrderType}</span>
            </Typography>
            
            {/* Price (for pending orders) */}
            {tab === 'pending' && (
              <TextField
                label="Price"
                type="number"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                fullWidth
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            )}

            {/* Volume - Only editable for PENDING orders */}
            {editingOrder?.Status === 'PENDING' && (
              <TextField
                label="Volume (Lots)"
                type="number"
                value={editVolume}
                onChange={(e) => setEditVolume(e.target.value)}
                fullWidth
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            )}

            {/* Stop Loss */}
            <TextField
              label="Stop Loss"
              type="number"
              value={editStopLoss}
              onChange={(e) => setEditStopLoss(e.target.value)}
              fullWidth
              InputLabelProps={{ sx: { color: '#787b86' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#2a2e39' },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                }
              }}
            />

            {/* Take Profit */}
            <TextField
              label="Take Profit"
              type="number"
              value={editTakeProfit}
              onChange={(e) => setEditTakeProfit(e.target.value)}
              fullWidth
              InputLabelProps={{ sx: { color: '#787b86' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#2a2e39' },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                }
              }}
            />

            {/* Risk/Reward Ratio Display */}
            {editRiskRewardRatio && (
              <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                <Typography sx={{ color: '#4ade80', fontSize: 11, fontWeight: 600, mb: 0.3 }}>
                  Risk:Reward {editRiskRewardRatio.ratio}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: 9, lineHeight: 1.4 }}>
                  Potential Gain: ${editRiskRewardRatio.reward} • Max Loss: ${editRiskRewardRatio.risk}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowEditModal(false)} 
            sx={{ 
              color: '#787b86',
              '&:hover': { bgcolor: '#2a2e39', color: '#fff' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateOrder}
            variant="contained"
            sx={{ 
              bgcolor: '#3b82f6',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#2563eb' }
            }}
          >
            Update Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog 
        open={showSuccessDialog} 
        onClose={() => setShowSuccessDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: '#131722',
            borderRadius: 2,
            border: '1px solid #2a2e39',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#131722', 
          color: '#fff', 
          borderBottom: '1px solid #2a2e39',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2.5
        }}>
          <Box sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: '50%', 
            bgcolor: 'rgba(16, 185, 129, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography sx={{ fontSize: 28 }}>✓</Typography>
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Success</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#131722', py: 3 }}>
          <Typography sx={{ 
            color: '#d1d4dc', 
            fontSize: 15,
            lineHeight: 1.8,
            whiteSpace: 'pre-line'
          }}>
            {successMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowSuccessDialog(false)}
            variant="contained"
            fullWidth
            sx={{ 
              bgcolor: '#10b981',
              color: '#fff',
              fontWeight: 600,
              py: 1.2,
              fontSize: 15,
              '&:hover': { bgcolor: '#059669' }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Real-time notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trade;

